import React from 'react';
import { useDashboard } from '../DashboardContext';

interface Props { compareMode: boolean; prevPeriodLabel: string; }

const TO_STUDENT = [
    { key: 'mentoring', label: 'Mentoring Guidance',   icon: '🎯', color: '#6366f1' },
    { key: 'jobs',      label: 'Jobs & Internships',   icon: '💼', color: '#0ea5e9' },
    { key: 'projects',  label: 'Short-term Projects',  icon: '🛠️', color: '#22c55e' },
];

const TO_INSTITUTE = [
    { key: 'advisory',   label: 'Advisory / Consulting', icon: '📋', color: '#f59e0b' },
    { key: 'curriculum', label: 'Curriculum Dev',         icon: '📚', color: '#8b5cf6' },
    { key: 'products',   label: 'Products & Services',    icon: '🏷️', color: '#ec4899' },
];

const ContributionRow = ({ item, count, pct }: { item: any; count: number; pct: number }) => (
    <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: 14 }}>{item.icon}</span>
                <span className="font-13">{item.label}</span>
            </div>
            <span className="fw-bold font-13">{count.toLocaleString()}</span>
        </div>
        <div className="progress" style={{ height: 5 }}>
            <div className="progress-bar" style={{ width: `${pct}%`, background: item.color }}></div>
        </div>
    </div>
);

const AlumniContributions: React.FC<Props> = ({ compareMode, prevPeriodLabel }) => {
    const { contributions, loading } = useDashboard();

    const allItems = [...TO_STUDENT, ...TO_INSTITUTE];

    // Progress bar: relative to the maximum value across all 6 types
    // (not total alumni) so bars are visually meaningful
    const maxCount = contributions
        ? Math.max(1, ...allItems.map(item => Number(contributions[item.key]) || 0))
        : 1;

    const getPct = (key: string) => {
        const count = Number(contributions?.[key]) || 0;
        return Math.round((count / maxCount) * 100);
    };

    return (
        <div className="card h-100">
            <div className="card-header bg-white">
                <div className="fw-semibold font-14">🤝 Alumni Contributions</div>
                <div className="text-muted small">Offer-to-student &amp; offer-to-institute</div>
            </div>
            <div className="card-body">
                {loading ? (
                    <div className="placeholder-glow">
                        {[1,2,3,4,5,6].map(i => (
                            <div key={i} className="d-flex align-items-center gap-2 mb-3">
                                <span className="placeholder rounded" style={{ width: 32, height: 32, display: 'block' }}></span>
                                <span className="placeholder col-8 d-block"></span>
                            </div>
                        ))}
                    </div>
                ) : !contributions ? (
                    <div className="text-center py-4 text-muted small">No contribution data available</div>
                ) : (
                    <div>
                        {/* Offer to student */}
                        <div className="small fw-semibold text-muted text-uppercase mb-2"
                            style={{ fontSize: 10, letterSpacing: '0.05em' }}>
                            Offer to Students
                        </div>
                        {TO_STUDENT.map(item => (
                            <ContributionRow
                                key={item.key}
                                item={item}
                                count={Number(contributions[item.key]) || 0}
                                pct={getPct(item.key)}
                            />
                        ))}

                        {/* Offer to institute */}
                        <div className="small fw-semibold text-muted text-uppercase mb-2 mt-3"
                            style={{ fontSize: 10, letterSpacing: '0.05em', borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                            Offer to Institute
                        </div>
                        {TO_INSTITUTE.map(item => (
                            <ContributionRow
                                key={item.key}
                                item={item}
                                count={Number(contributions[item.key]) || 0}
                                pct={getPct(item.key)}
                            />
                        ))}

                        {/* Compare row */}
                        {compareMode && (
                            <div className="mt-3 pt-2 border-top text-muted small">
                                {prevPeriodLabel}: comparison coming soon
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AlumniContributions;
